import { useCallback, useEffect, useId, useRef, useState } from 'react';
import Button from '~/components2/forms/Button';
import type { IconName } from '~/components2/Icon';
import { containerClassName } from './containerClasses';
import {
  canPan,
  clampPan,
  MAX_SCALE,
  MIN_SCALE,
  nextScaleFromStep,
  nextScaleFromWheelDelta,
  type PanDirection,
  type Point,
  panByStep,
} from './zoomMath';

export type { PanDirection } from './zoomMath';
export type PanMethod = 'button' | 'keyboard';

interface Props {
  src: string;
  alt: string;
  className?: string;
  minScale?: number;
  maxScale?: number;
  testIdPrefix?: string;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  onZoomStep?: (direction: 1 | -1) => void;
  onPanStep?: (direction: PanDirection, method: PanMethod) => void;
}

const ORIGIN: Point = { x: 0, y: 0 };

const PAN_KEYS: Record<string, PanDirection> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

const PAN_BUTTONS: { direction: PanDirection; icon: IconName; label: string; className: string }[] = [
  { direction: 'up', icon: 'chevronUp', label: 'Pan up', className: 'col-start-2 row-start-1' },
  { direction: 'right', icon: 'chevronRight', label: 'Pan right', className: 'col-start-3 row-start-2' },
  { direction: 'down', icon: 'chevronDown', label: 'Pan down', className: 'col-start-2 row-start-3' },
  { direction: 'left', icon: 'chevronLeft', label: 'Pan left', className: 'col-start-1 row-start-2' },
];

export default function ZoomableImage({
  src,
  alt,
  className,
  minScale = MIN_SCALE,
  maxScale = MAX_SCALE,
  testIdPrefix = 'zoomable-image',
  onFullscreenChange,
  onZoomStep,
  onPanStep,
}: Props) {
  const hintId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const panControlsRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragStateRef = useRef<{ startX: number; startY: number; startPan: Point } | null>(null);
  const isFullscreenRef = useRef(false);
  const onFullscreenChangeRef = useRef(onFullscreenChange);
  onFullscreenChangeRef.current = onFullscreenChange;

  const [scale, setScale] = useState(minScale);
  const [pan, setPan] = useState<Point>(ORIGIN);
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const containerSize = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    return { w: rect?.width ?? 0, h: rect?.height ?? 0 };
  }, []);

  // offsetWidth/offsetHeight ignore the CSS `transform: scale(...)` below (unlike
  // getBoundingClientRect), so this reflects the image's actual unscaled rendered size.
  const renderedImageSize = useCallback(() => {
    const img = imgRef.current;
    return { w: img?.offsetWidth ?? 0, h: img?.offsetHeight ?? 0 };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const next = document.fullscreenElement === containerRef.current;
      const prev = isFullscreenRef.current;
      isFullscreenRef.current = next;
      setIsFullscreen(next);
      if (prev !== next) {
        onFullscreenChangeRef.current?.(next);
      }
      // Entering/leaving fullscreen resizes both the container and the letterboxed image,
      // so a pan that was in bounds can now overshoot. Wait for the fullscreen-state class
      // to render before measuring, otherwise the clamp uses the pre-transition boxes.
      requestAnimationFrame(() => {
        setPan((current) => clampPan(current, scale, renderedImageSize(), containerSize(), minScale));
      });
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [scale, minScale, containerSize, renderedImageSize]);

  // The pan buttons unmount at the minimum scale, so focus on one moves to the viewer rather than <body>.
  const keepFocusOnZoomOut = useCallback(() => {
    if (panControlsRef.current?.contains(document.activeElement)) containerRef.current?.focus();
  }, []);

  const applyScale = useCallback(
    (next: number) => {
      setScale(next);
      if (next <= minScale) {
        keepFocusOnZoomOut();
        setPan(ORIGIN);
      } else {
        setPan((prev) => clampPan(prev, next, renderedImageSize(), containerSize(), minScale));
      }
    },
    [minScale, containerSize, renderedImageSize, keepFocusOnZoomOut],
  );

  // React registers wheel listeners as passive, silently no-op'ing preventDefault for real
  // (trusted) wheel input — attach a native listener directly so it actually takes effect.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      applyScale(nextScaleFromWheelDelta(scale, e.deltaY, undefined, minScale, maxScale));
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [scale, minScale, maxScale, applyScale]);

  const stepZoom = (direction: 1 | -1) => {
    applyScale(nextScaleFromStep(scale, direction, undefined, minScale, maxScale));
    onZoomStep?.(direction);
  };

  const stepPan = (direction: PanDirection, method: PanMethod) => {
    if (!canPan(pan, direction, scale, renderedImageSize(), containerSize(), minScale)) return;
    setPan(panByStep(pan, direction, scale, renderedImageSize(), containerSize(), minScale));
    onPanStep?.(direction, method);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only the container itself; arrow keys on the overlay buttons keep their default behavior.
    if (e.target !== e.currentTarget) return;
    if (e.metaKey || e.ctrlKey || e.altKey || e.nativeEvent.isComposing) return;
    const direction = PAN_KEYS[e.key];
    if (!direction || scale <= minScale) return;
    e.preventDefault();
    stepPan(direction, 'keyboard');
  };

  const isZoomed = scale > minScale;

  const resetZoom = () => {
    keepFocusOnZoomOut();
    setScale(minScale);
    setPan(ORIGIN);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (scale <= minScale) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, startPan: pan };
    setIsDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    const next = {
      x: dragState.startPan.x + (e.clientX - dragState.startX),
      y: dragState.startPan.y + (e.clientY - dragState.startY),
    };
    setPan(clampPan(next, scale, renderedImageSize(), containerSize(), minScale));
  };

  const endDrag = () => {
    dragStateRef.current = null;
    setIsDragging(false);
  };

  const toggleFullscreen = () => {
    // Both can reject — swallow rather than let it surface as an unhandled rejection.
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      containerRef.current?.requestFullscreen().catch(() => {});
    }
  };

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: arrow keys pan the photo while this named region has focus; every pan is also available from the pan buttons.
    // biome-ignore lint/a11y/useSemanticElements: a fieldset groups form controls; this is a named viewer region, not a form.
    <div
      ref={containerRef}
      role="group"
      aria-label="Photo viewer"
      aria-describedby={hintId}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the photo region is a Tab stop so arrow keys can pan it.
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={`${containerClassName(className, isFullscreen)} outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-3 focus-visible:outline-focus`}
      style={{ touchAction: 'none' }}
      data-testid={`${testIdPrefix}-container`}
    >
      <span id={hintId} className="sr-only">
        Zoom in to pan the photo with the arrow keys or the pan buttons.
      </span>
      {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: drag-to-pan/double-click-reset on the photo itself; zoom is also reachable via the keyboard-accessible +/-/fullscreen buttons below. Handlers live on the image (not the container) so clicks on the sibling overlay buttons never bubble into pointer-capture/drag logic here. */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        data-testid={`${testIdPrefix}-image`}
        className="max-h-full max-w-full select-none object-contain"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          cursor: scale > minScale ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onDoubleClick={resetZoom}
      />

      <div className="absolute bottom-3 left-3 z-10 flex items-end gap-2">
        <div className="flex flex-col gap-2">
          <Button
            alternate
            icon="plus"
            iconSize={14}
            size="icon"
            onClick={() => stepZoom(1)}
            aria-label="Zoom in"
            data-testid={`${testIdPrefix}-zoom-in`}
          />
          <Button
            alternate
            icon="minus"
            iconSize={14}
            size="icon"
            onClick={() => stepZoom(-1)}
            aria-label="Zoom out"
            data-testid={`${testIdPrefix}-zoom-out`}
          />
        </div>
      </div>

      {isZoomed && (
        <div className="absolute bottom-3 right-3 z-10">
          <div
            ref={panControlsRef}
            className="grid grid-cols-3 grid-rows-3 gap-1"
            data-testid={`${testIdPrefix}-pan-controls`}
          >
            {PAN_BUTTONS.map(({ direction, icon, label, className: cellClassName }) => {
              const isAtEdge = !canPan(pan, direction, scale, renderedImageSize(), containerSize(), minScale);
              return (
                // aria-disabled instead of disabled, so reaching an edge doesn't drop focus from the pressed button.
                <Button
                  key={direction}
                  alternate
                  icon={icon}
                  iconSize={14}
                  size="icon"
                  onClick={() => stepPan(direction, 'button')}
                  aria-label={label}
                  aria-disabled={isAtEdge || undefined}
                  className={`${cellClassName} aria-disabled:cursor-not-allowed aria-disabled:border-transparent aria-disabled:bg-grey-20 aria-disabled:text-grey-50 aria-disabled:hover:bg-grey-20`}
                  data-testid={`${testIdPrefix}-pan-${direction}`}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="absolute right-3 top-3 z-10">
        <Button
          alternate
          icon={isFullscreen ? 'minimize' : 'maximize'}
          iconSize={14}
          size="icon"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
          data-testid={`${testIdPrefix}-fullscreen-toggle`}
        />
      </div>
    </div>
  );
}
